import axios, { create } from 'axios';
import getBaseURL, { getBaseUrlForAdminPanel, getBaseUrlForOwner } from './getBaseURL';

const timeout = 600000;

/** @type {import('axios').AxiosInstance} */
const axiosInstance = create({ baseURL: getBaseURL(), timeout });
const axiosInstanceForOwner = create({ baseURL: getBaseUrlForOwner(), timeout });
let adminPanelClient;
const defaultErrorInterceptor = error => Promise.reject(error);
const defaultRequestInterceptor = config => config;
const defaultResponseInterceptor = response => response;

export function setHeader(key, value) {
  axiosInstance.defaults.headers.common[key] = value;
}

export function setHeaderForOwner(key, value) {
  axiosInstanceForOwner.defaults.headers.common[key] = value;
}

export function unsetHeader(key) {
  delete axiosInstance.defaults.headers.common[key];
}

export function unsetHeaderForOwner(key) {
  delete axiosInstanceForOwner.defaults.headers.common[key];
}

export function addRequestInterceptor({ request, error }) {
  const interceptor = axiosInstance.interceptors.request.use(
    request || defaultRequestInterceptor,
    error || defaultErrorInterceptor
  );
  return () => {
    axiosInstance.interceptors.request.eject(interceptor);
  };
}

export function addRequestInterceptorForOwner({ request, error }) {
  const interceptor = axiosInstanceForOwner.interceptors.request.use(
    request || defaultRequestInterceptor,
    error || defaultErrorInterceptor
  );
  return () => {
    axiosInstanceForOwner.interceptors.request.eject(interceptor);
  };
}

export function addResponseInterceptor({ response, error }) {
  const interceptor = axiosInstance.interceptors.response.use(
    response || defaultResponseInterceptor,
    error || defaultErrorInterceptor
  );
  return () => {
    axiosInstance.interceptors.response.eject(interceptor);
  };
}

export function addResponseInterceptorForOwner({ response, error }) {
  const interceptor = axiosInstanceForOwner.interceptors.response.use(
    response || defaultResponseInterceptor,
    error || defaultErrorInterceptor
  );
  return () => {
    axiosInstanceForOwner.interceptors.response.eject(interceptor);
  };
}

/**
 * @return {Promise<any>}
 */
const get = (url, config) => {
  return axiosInstance.get(url, config).then(response => response.data);
};

const getForOwner = (url, config) => {
  return axiosInstanceForOwner.get(url, config).then(response => response.data);
};

const post = (url, data, config) => {
  return axiosInstance.post(url, data, config).then(response => response.data);
};

const postForOwner = (url, data, config) => {
  return axiosInstanceForOwner.post(url, data, config).then(response => response.data);
};

const client = {
  get,
  post,
  postRaw: axiosInstance.post,
  getRaw: axiosInstance.get,
  delete: axiosInstance.delete,
  put: axiosInstance.put,
  patch: axiosInstance.patch,
  request: axiosInstance.request,
  getCancelToken: axios.CancelToken.source,
};

export const clientForOwner = {
  get: getForOwner,
  post: postForOwner,
  postRaw: axiosInstanceForOwner.post,
  getRaw: axiosInstanceForOwner.get,
  delete: axiosInstanceForOwner.delete,
  put: axiosInstanceForOwner.put,
  patch: axiosInstanceForOwner.patch,
  request: axiosInstanceForOwner.request,
  getCancelToken: axios.CancelToken.source,
};

export function getAdminPanelClient() {
  if (!adminPanelClient) {
    const instance = create({ baseURL: getBaseUrlForAdminPanel(), timeout });

    adminPanelClient = {
      get: (url, config) => instance.get(url, config).then(response => response.data),
      post: (url, data, config) => instance.post(url, data, config).then(response => response.data),
      postRaw: instance.post,
      getRaw: instance.get,
      delete: instance.delete,
      put: instance.put,
      patch: instance.patch,
      request: instance.request,
      getCancelToken: axios.CancelToken.source,
      setHeader: (key, value) => {
        instance.defaults.headers.common[key] = value;
      },
      unsetHeader: key => {
        delete instance.defaults.headers.common[key];
      },
      addRequestInterceptor: ({ request, error }) =>
        instance.interceptors.request.use(request || defaultRequestInterceptor, error || defaultErrorInterceptor),
      addResponseInterceptor: ({ response, error }) =>
        instance.interceptors.response.use(response || defaultResponseInterceptor, error || defaultErrorInterceptor),
    };
  }

  return adminPanelClient;
}

export default client;
